import { useAuthContext, type AuthErrorKind } from '@/contexts/AuthContext'
import type { AuthState } from '@/lib/auth'
import type { AppLocale } from '@/i18n/routing'

/**
 * Hook to access authenticated user state.
 * Must be used within an AuthProvider.
 *
 * @returns Auth state with userId, profile, and loading status
 */
export function useAuth(): {
  auth: AuthState | null
  isLoading: boolean
  error: Error | null
  errorKind: AuthErrorKind | null
  userId: string | null
  profile: AuthState['profile'] | null
  email: string | null
  isAnonymous: boolean
  isAuthenticated: boolean
  isEditor: boolean
  editorLocales: AppLocale[]
  refreshAuth: () => Promise<void>
  retryInit: () => Promise<void>
} {
  const { auth, isLoading, error, errorKind, refreshAuth, retryInit } = useAuthContext()
  const editorLocales = auth?.profile?.editorLocales ?? []

  return {
    auth,
    isLoading,
    error,
    errorKind,
    userId: auth?.userId ?? null,
    profile: auth?.profile ?? null,
    email: auth?.profile?.email ?? null,
    isAnonymous: auth?.isAnonymous ?? true,
    isAuthenticated: !!auth?.userId,
    isEditor: editorLocales.length > 0,
    editorLocales,
    refreshAuth,
    retryInit,
  }
}
